import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api, { DashboardData, RecentSurvey, formatMMK, formatMMKShort, ADMIN_ROLES } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import { Users, Handshake, DollarSign, Trophy, Wrench, AlertTriangle, MapPin, LayoutGrid, Send, CheckCircle, XCircle } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { formatDateOnly } from '@/lib/dateUtils'

const ENGINEERING_ROLES = ['chief_engineer', 'operation_manager', 'engineer', 'technician', 'procurement', 'storekeeper']

type TestState = { status: 'idle' | 'loading' | 'ok' | 'error'; message?: string }

export default function Dashboard() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const isEngineeringRole = ENGINEERING_ROLES.includes(user?.role || '')
  const isAdmin = (ADMIN_ROLES as string[]).includes(user?.role || '')
  const [testStates, setTestStates] = useState<Record<string, TestState>>({ sr: { status: 'idle' }, br: { status: 'idle' }, ec: { status: 'idle' } })

  async function sendTestNotification(channel: string) {
    setTestStates(prev => ({ ...prev, [channel]: { status: 'loading' } }))
    try {
      const res = await api.post(`/admin/test-notification?channel=${channel}`)
      if (res.data.ok) {
        setTestStates(prev => ({ ...prev, [channel]: { status: 'ok', message: 'Test card sent successfully' } }))
      } else {
        setTestStates(prev => ({ ...prev, [channel]: { status: 'error', message: res.data.error || 'Failed to send' } }))
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Request failed'
      setTestStates(prev => ({ ...prev, [channel]: { status: 'error', message: msg } }))
    }
    setTimeout(() => setTestStates(prev => ({ ...prev, [channel]: { status: 'idle' } })), 5000)
  }

  const { data, isLoading } = useQuery<DashboardData>({
    queryKey: ['dashboard'],
    queryFn: () => api.get('/dashboard').then(r => r.data),
  })

  const { data: appSettings } = useQuery<{ project_management_enabled?: boolean }>({
    queryKey: ['app-settings'],
    queryFn: () => api.get('/project-management/settings').then(r => r.data),
    staleTime: 60000,
  })

  const togglePM = useMutation({
    mutationFn: (enabled: boolean) => api.patch('/project-management/settings', { project_management_enabled: enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-settings'] })
    },
  })

  const pmEnabled = appSettings?.project_management_enabled === true

  if (isLoading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-800"></div></div>
  if (!data) return null

  const stages = ['Qualification', 'Need Analysis', 'Proposal', 'Negotiation', 'Won', 'Lost']
  const chartData = stages.map(s => ({
    stage: s.replace('Need Analysis', 'Analysis'),
    count: data.pipeline_summary[s]?.count || 0,
    value: data.pipeline_summary[s]?.value || 0,
  }))

  const salesMetrics = [
    { label: 'Leads', value: data.leads, icon: Users, color: 'bg-red-700' },
    { label: 'Contacts', value: data.contacts, icon: Users, color: 'bg-red-700' },
    { label: 'Pipeline Value', value: formatMMK(data.pipeline_value), icon: DollarSign, color: 'bg-purple-500' },
    { label: 'Won Deals', value: data.won_deals, icon: Trophy, color: 'bg-amber-500' },
    { label: 'Won Value', value: formatMMK(data.won_value), icon: DollarSign, color: 'bg-green-500' },
  ]

  const operationalMetrics = [
    { label: 'Open SRs', value: data.open_srs, icon: Wrench, color: 'bg-red-500' },
  ]

  const visibleMetrics = isEngineeringRole ? operationalMetrics : [...salesMetrics, ...operationalMetrics]

  return (
    <div className="space-y-6">
      <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Dashboard</h1>

      {isAdmin && (
        <div className="space-y-3">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-2 rounded-lg">
                <LayoutGrid size={18} className="text-purple-700" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900">Project Management Module</div>
                <div className="text-xs text-slate-500">Kanban board and timeline view for all users</div>
              </div>
            </div>
            <button
              onClick={() => togglePM.mutate(!pmEnabled)}
              disabled={togglePM.isPending}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${pmEnabled ? 'bg-green-500' : 'bg-slate-300'} ${togglePM.isPending ? 'opacity-50' : ''}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${pmEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Send size={18} className="text-blue-700" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900">Teams Webhook Channels</div>
                <div className="text-xs text-slate-500">Send a test card to verify each channel is connected</div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {([
                { key: 'sr', label: 'SR / SS Channel' },
                { key: 'br', label: 'Budget Requests' },
                { key: 'ec', label: 'Expense Claims' },
              ] as const).map(({ key, label }) => {
                const ts = testStates[key]
                return (
                  <div key={key} className="flex flex-col gap-1">
                    <button
                      onClick={() => sendTestNotification(key)}
                      disabled={ts.status === 'loading'}
                      className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 bg-slate-50 hover:bg-slate-100 disabled:opacity-50 transition-colors"
                    >
                      {ts.status === 'loading' ? (
                        <span className="animate-spin inline-block h-3.5 w-3.5 border-2 border-slate-400 border-t-transparent rounded-full" />
                      ) : (
                        <Send size={13} />
                      )}
                      Send Test — {label}
                    </button>
                    {ts.status === 'ok' && (
                      <div className="flex items-center gap-1 text-xs text-green-700">
                        <CheckCircle size={12} /> {ts.message}
                      </div>
                    )}
                    {ts.status === 'error' && (
                      <div className="flex items-start gap-1 text-xs text-red-600">
                        <XCircle size={12} className="mt-0.5 shrink-0" /> {ts.message}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      <div className={`grid gap-3 sm:gap-4 ${isEngineeringRole ? 'grid-cols-1 max-w-xs' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6'}`}>
        {visibleMetrics.map(m => (
          <div key={m.label} className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className={`${m.color} p-2 rounded-lg text-white`}><m.icon size={18} /></div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{m.value}</div>
                <div className="text-xs text-slate-500">{m.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {!isEngineeringRole && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
            <h2 className="font-semibold text-slate-900 mb-4">Pipeline Overview</h2>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="stage" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(val: number, name: string) => [name === 'value' ? formatMMK(val) : val, name === 'value' ? 'Value' : 'Deals']} />
                <Bar dataKey="count" name="Deals" fill="#b71c1c" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
            <h2 className="font-semibold text-slate-900 mb-4">
              <AlertTriangle size={16} className="inline mr-2 text-amber-500" />
              Expiry Alerts ({data.expiry_alerts.length})
            </h2>
            {data.expiry_alerts.length === 0 ? (
              <p className="text-sm text-slate-500">No upcoming expiry alerts</p>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {data.expiry_alerts.map(p => (
                  <div key={p.id} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg text-sm">
                    <div>
                      <span className="font-medium text-slate-900">{p.project_type}-{p.project_code}</span>
                      <span className="text-slate-500 ml-2">{p.client_name}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p._expiry_status === 'Expired' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                      {p.service_expiry_date}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
        <h2 className="font-semibold text-slate-900 mb-4">Recent Service Requests</h2>
        {data.recent_srs.length === 0 ? (
          <p className="text-sm text-slate-500">No service requests</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3 font-medium text-slate-600">SR#</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Client</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Category</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Priority</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Status</th>
              </tr></thead>
              <tbody>
                {data.recent_srs.map(sr => (
                  <tr key={sr.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3 font-medium">{sr.sr_number}</td>
                    <td className="py-2 px-3">{sr.client_name}</td>
                    <td className="py-2 px-3">{sr.category}</td>
                    <td className="py-2 px-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sr.priority === 'Critical' ? 'bg-red-100 text-red-700' : sr.priority === 'High' ? 'bg-orange-100 text-orange-700' : sr.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-600'}`}>{sr.priority}</span>
                    </td>
                    <td className="py-2 px-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sr.status === 'Open' ? 'bg-red-100 text-red-900' : sr.status === 'In Progress' ? 'bg-purple-100 text-purple-700' : sr.status === 'Resolved' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>{sr.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
        <h2 className="font-semibold text-slate-900 mb-4">
          <MapPin size={16} className="inline mr-2 text-red-800" />
          Recent Site Surveys ({data.recent_surveys.length})
        </h2>
        {data.recent_surveys.length === 0 ? (
          <p className="text-sm text-slate-500">No site surveys yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3 font-medium text-slate-600">SS#</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Client</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Status</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Date</th>
              </tr></thead>
              <tbody>
                {data.recent_surveys.map((ss: RecentSurvey) => (
                  <tr key={ss.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3 font-medium">{ss.sr_number}</td>
                    <td className="py-2 px-3">{ss.client_name}</td>
                    <td className="py-2 px-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ss.status === 'Pending' ? 'bg-amber-100 text-amber-700' : ss.status === 'Scheduled' ? 'bg-blue-100 text-blue-700' : ss.status === 'In Progress' ? 'bg-purple-100 text-purple-700' : ss.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>{ss.status}</span>
                    </td>
                    <td className="py-2 px-3 text-slate-500">{formatDateOnly(ss.reported_date || ss.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
